import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FirebaseError } from "firebase/app";
import {
  Timestamp,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type QueryDocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { fetchBoards, getBoards, getTournoi } from "../../../features/tournoi/tournoiSlice";
import { showNotification } from "../../../features/globalNotification/globalNotificationSlice";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  computeIsFull,
  computePlacesRestantes,
  normalizeTableauFromCallable,
  normalizeWaitlistCount,
  Players,
  Tableau,
} from "../../../types";
import cloudFunctions from "../../../services/firebaseCloudFunctions";
import firestoreInstance from "../../../services/firestoreInstance";
import ConfirmDialog from "../../Base/ConfirmDialog";

type WaitlistEntry = {
  id: string;
  waitlistPosition?: number;
  playerId: string;
  playerName: string;
  playerSurname: string;
  licenceId: string;
  club?: string;
  createdAt?: Date;
  present?: boolean;
  presentAt?: Date;
};

type WaitlistEntryFirestore = {
  waitlistPosition?: number;
  playerId: string;
  playerName: string;
  playerSurname: string;
  licenceId: string;
  club?: string;
  createdAt?: Timestamp;
  present?: boolean;
  presentAt?: Timestamp;
};

type WaitlistEntryWrite = Omit<
  WaitlistEntryFirestore,
  "createdAt" | "present" | "presentAt"
> & {
  createdAt: ReturnType<typeof serverTimestamp>;
  present: false;
};

const sortWaitlistEntries = (entries: WaitlistEntry[]): WaitlistEntry[] => {
  return [...entries].sort((a, b) => {
    const posA = a.waitlistPosition ?? Number.MAX_SAFE_INTEGER;
    const posB = b.waitlistPosition ?? Number.MAX_SAFE_INTEGER;
    if (posA !== posB) return posA - posB;
    const dateA = a.createdAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const dateB = b.createdAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
    if (dateA !== dateB) return dateA - dateB;
    return `${a.playerName} ${a.playerSurname}`.localeCompare(
      `${b.playerName} ${b.playerSurname}`
    );
  });
};

const mapWaitlistSnapshot = (
  docs: QueryDocumentSnapshot<DocumentData>[]
): WaitlistEntry[] =>
  sortWaitlistEntries(
    docs.map((docSnap) => {
      const data = docSnap.data() as WaitlistEntryFirestore;
      return {
        id: docSnap.id,
        waitlistPosition:
          typeof data.waitlistPosition === "number"
            ? data.waitlistPosition
            : undefined,
        playerId: data.playerId,
        playerName: data.playerName,
        playerSurname: data.playerSurname,
        licenceId: data.licenceId,
        club: data.club,
        createdAt: data.createdAt?.toDate(),
        present: data.present === true,
        presentAt: data.presentAt?.toDate(),
      };
    })
  );

const boardHasWaitlistActivity = (board: Tableau): boolean =>
  Boolean(board.isFull) || normalizeWaitlistCount(board.waitlistCount) > 0;

const AdminWaitlist: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const boards = useAppSelector(getBoards);
  const tournoi = useAppSelector(getTournoi);

  const [selectedBoardId, setSelectedBoardId] = useState<string>("");
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [loadingBoards, setLoadingBoards] = useState<boolean>(true);
  const [loadingList, setLoadingList] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [loadingPlayer, setLoadingPlayer] = useState<boolean>(false);
  const [addingPlayer, setAddingPlayer] = useState<boolean>(false);
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null);
  const [updatingPresentId, setUpdatingPresentId] = useState<string | null>(
    null
  );
  const [entryToDelete, setEntryToDelete] = useState<WaitlistEntry | null>(null);

  const [licenceInput, setLicenceInput] = useState<string>("");
  const [foundPlayer, setFoundPlayer] = useState<Players | null>(null);
  const [eligibleFullBoards, setEligibleFullBoards] = useState<Tableau[]>([]);
  const [targetBoardIdForAdd, setTargetBoardIdForAdd] = useState<string>("");
  const [alreadyInTargetWaitlist, setAlreadyInTargetWaitlist] =
    useState<boolean>(false);
  const [alreadyRegisteredOnTargetBoard, setAlreadyRegisteredOnTargetBoard] =
    useState<boolean>(false);
  const [playerSearchDone, setPlayerSearchDone] = useState<boolean>(false);

  const fullBoards = useMemo(
    () =>
      boards
        .filter(boardHasWaitlistActivity)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [boards]
  );
  const selectedBoard = useMemo(
    () => fullBoards.find((board) => String(board.id) === selectedBoardId),
    [fullBoards, selectedBoardId]
  );
  const presentCount = useMemo(
    () => waitlistEntries.filter((entry) => entry.present).length,
    [waitlistEntries]
  );
  const isDark = theme.palette.mode === "dark";

  const getBoardCapacityLabel = (board: Tableau): string => {
    if (board.isFull) {
      return "Complet";
    }
    const remaining = computePlacesRestantes(board);
    return `${remaining} place${remaining > 1 ? "s" : ""} restante${
      remaining > 1 ? "s" : ""
    }`;
  };

  const getBoardCapacityColor = (board: Tableau): "warning" | "success" =>
    computeIsFull(board) ? "warning" : "success";

  useEffect(() => {
    const loadBoards = async () => {
      setLoadingBoards(true);
      try {
        await dispatch(fetchBoards()).unwrap();
      } finally {
        setLoadingBoards(false);
      }
    };
    void loadBoards();
  }, [dispatch]);

  useEffect(() => {
    const checkAlreadyInTargetWaitlist = async () => {
      if (!tournoi?.id || !foundPlayer?.id || !targetBoardIdForAdd) {
        setAlreadyInTargetWaitlist(false);
        return;
      }
      const waitlistRef = doc(
        firestoreInstance,
        "tournois",
        tournoi.id,
        "tableaux",
        targetBoardIdForAdd,
        "waitingList",
        String(foundPlayer.id)
      );
      const snap = await getDoc(waitlistRef);
      setAlreadyInTargetWaitlist(snap.exists());
    };

    void checkAlreadyInTargetWaitlist();
  }, [foundPlayer?.id, targetBoardIdForAdd, tournoi?.id]);

  useEffect(() => {
    const checkAlreadyRegisteredOnTargetBoard = async () => {
      if (!tournoi?.id || !foundPlayer?.id || !targetBoardIdForAdd) {
        setAlreadyRegisteredOnTargetBoard(false);
        return;
      }
      const participationSnap = await getDocs(
        query(
          collection(firestoreInstance, "tournois", tournoi.id, "participation"),
          where("playersId", "==", String(foundPlayer.id))
        )
      );
      const isAlreadyRegistered = participationSnap.docs.some((docSnap) => {
        const data = docSnap.data() as { tableauId?: string };
        return data.tableauId === targetBoardIdForAdd;
      });
      setAlreadyRegisteredOnTargetBoard(isAlreadyRegistered);
    };

    void checkAlreadyRegisteredOnTargetBoard();
  }, [foundPlayer?.id, targetBoardIdForAdd, tournoi?.id]);

  useEffect(() => {
    if (!selectedBoardId && fullBoards.length > 0) {
      setSelectedBoardId(String(fullBoards[0].id));
    }
    if (fullBoards.length === 0) {
      setSelectedBoardId("");
    }
  }, [fullBoards, selectedBoardId]);

  const loadWaitlist = useCallback(async () => {
    if (!tournoi?.id || !selectedBoardId) {
      setWaitlistEntries([]);
      return;
    }
    setLoadingList(true);
    try {
      const snapshot = await getDocs(
        query(
          collection(
            firestoreInstance,
            "tournois",
            tournoi.id,
            "tableaux",
            selectedBoardId,
            "waitingList"
          )
        )
      );

      setWaitlistEntries(mapWaitlistSnapshot(snapshot.docs));
    } catch (error) {
      dispatch(
        showNotification({
          message: "Impossible de charger la liste d'attente.",
          severity: "error",
        })
      );
    } finally {
      setLoadingList(false);
    }
  }, [dispatch, selectedBoardId, tournoi?.id]);

  useEffect(() => {
    void loadWaitlist();
  }, [loadWaitlist]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dispatch(fetchBoards()).unwrap();
      await loadWaitlist();
    } catch {
      dispatch(
        showNotification({
          message: "Impossible d'actualiser les listes d'attente.",
          severity: "error",
        })
      );
    } finally {
      setRefreshing(false);
    }
  };

  const handleBoardChange = (event: SelectChangeEvent<string>) => {
    setSelectedBoardId(event.target.value);
  };

  const handleSearchPlayer = async () => {
    const licenceId = licenceInput.trim();
    if (!licenceId || !tournoi?.id) {
      return;
    }
    setLoadingPlayer(true);
    setFoundPlayer(null);
    setEligibleFullBoards([]);
    setTargetBoardIdForAdd("");
    setAlreadyRegisteredOnTargetBoard(false);
    setPlayerSearchDone(false);
    try {
      const findPlayerFromLicence = httpsCallable<
        { licenceId: string; tournamentId: string },
        Players
      >(cloudFunctions, "findplayerfromlicence");
      const playerResponse = await findPlayerFromLicence({
        licenceId,
        tournamentId: tournoi.id,
      });
      const player = playerResponse.data;

      const accessibleBoards = httpsCallable<
        { playerId: string; tournamentId: string },
        unknown[]
      >(cloudFunctions, "accessibleBoards");
      const boardsResponse = await accessibleBoards({
        playerId: String(player.id),
        tournamentId: tournoi.id,
      });
      const accessible = boardsResponse.data.map((board) =>
        normalizeTableauFromCallable(board)
      );
      const eligible = accessible
        .filter(
          (board) =>
            board.hasWaitingQueue &&
            (Boolean(board.isFull) || normalizeWaitlistCount(board.waitlistCount) > 0)
        )
        .sort((a, b) => a.name.localeCompare(b.name));

      setFoundPlayer(player);
      setEligibleFullBoards(eligible);
      if (eligible.length > 0) {
        const sameAsSelected = eligible.find(
          (board) => String(board.id) === selectedBoardId
        );
        setTargetBoardIdForAdd(
          String(sameAsSelected?.id ?? eligible[0].id ?? "")
        );
      }
    } catch (error) {
      let message = "Joueur introuvable pour ce numéro de licence.";
      if (
        error instanceof FirebaseError &&
        error.code !== "functions/not-found" &&
        error.message
      ) {
        message = error.message;
      }
      dispatch(
        showNotification({
          message,
          severity: "error",
        })
      );
    } finally {
      setPlayerSearchDone(true);
      setLoadingPlayer(false);
    }
  };

  const handleAddToWaitlist = async () => {
    if (!tournoi?.id || !foundPlayer?.id || !targetBoardIdForAdd) {
      return;
    }

    const targetBoard = boards.find(
      (board) => String(board.id) === targetBoardIdForAdd
    );
    if (!targetBoard?.hasWaitingQueue) {
      dispatch(
        showNotification({
          message:
            "La file d'attente n'est pas activée sur ce tableau. Activez « Tableau avec file d'attente » dans Capacité et ouverture.",
          severity: "warning",
        })
      );
      return;
    }

    setAddingPlayer(true);
    try {
      const waitlistRef = doc(
        firestoreInstance,
        "tournois",
        tournoi.id,
        "tableaux",
        targetBoardIdForAdd,
        "waitingList",
        String(foundPlayer.id)
      );
      const boardRef = doc(
        firestoreInstance,
        "tournois",
        tournoi.id,
        "tableaux",
        targetBoardIdForAdd
      );

      const existing = await getDoc(waitlistRef);
      if (existing.exists()) {
        setAlreadyInTargetWaitlist(true);
        dispatch(
          showNotification({
            message: "Ce joueur est déjà en liste d'attente sur ce tableau.",
            severity: "warning",
          })
        );
        return;
      }

      const participationSnap = await getDocs(
        query(
          collection(firestoreInstance, "tournois", tournoi.id, "participation"),
          where("playersId", "==", String(foundPlayer.id))
        )
      );
      const isAlreadyRegistered = participationSnap.docs.some((docSnap) => {
        const data = docSnap.data() as { tableauId?: string };
        return data.tableauId === targetBoardIdForAdd;
      });
      if (isAlreadyRegistered) {
        setAlreadyRegisteredOnTargetBoard(true);
        dispatch(
          showNotification({
            message:
              "Ce joueur est déjà inscrit sur ce tableau. Ajout en liste d'attente impossible.",
            severity: "warning",
          })
        );
        return;
      }

      const waitingListCol = collection(
        firestoreInstance,
        "tournois",
        tournoi.id,
        "tableaux",
        targetBoardIdForAdd,
        "waitingList"
      );
      const waitingListSnap = await getDocs(waitingListCol);
      const maxPosition = waitingListSnap.docs.reduce((max: number, docSnap) => {
        const data = docSnap.data() as WaitlistEntryFirestore;
        const pos =
          typeof data.waitlistPosition === "number" ? data.waitlistPosition : 0;
        return Math.max(max, pos);
      }, 0);
      const nextPosition = maxPosition + 1;

      const batch = writeBatch(firestoreInstance);
      batch.set(waitlistRef, {
        waitlistPosition: nextPosition,
        playerId: String(foundPlayer.id),
        playerName: foundPlayer.name,
        playerSurname: foundPlayer.surname,
        licenceId: foundPlayer.licenceId,
        club: foundPlayer.club ?? "",
        createdAt: serverTimestamp(),
        present: false,
      } satisfies WaitlistEntryWrite);
      batch.update(boardRef, { waitlistCount: increment(1) });
      await batch.commit();

      dispatch(
        showNotification({
          message: "Joueur ajouté en liste d'attente.",
          severity: "success",
        })
      );
      setAlreadyInTargetWaitlist(true);
      await dispatch(fetchBoards()).unwrap();

      if (targetBoardIdForAdd === selectedBoardId) {
        const snapshot = await getDocs(
          query(
            collection(
              firestoreInstance,
              "tournois",
              tournoi.id,
              "tableaux",
              selectedBoardId,
              "waitingList"
            )
          )
        );
        setWaitlistEntries(mapWaitlistSnapshot(snapshot.docs));
      }
    } catch (_error) {
      if (_error instanceof Error && _error.message === "already-exists") {
        setAlreadyInTargetWaitlist(true);
        dispatch(
          showNotification({
            message: "Ce joueur est déjà en liste d'attente sur ce tableau.",
            severity: "warning",
          })
        );
        return;
      }
      dispatch(
        showNotification({
          message: "Impossible d'ajouter ce joueur en liste d'attente.",
          severity: "error",
        })
      );
    } finally {
      setAddingPlayer(false);
    }
  };

  const handleTogglePresent = async (entry: WaitlistEntry, present: boolean) => {
    if (!tournoi?.id || !selectedBoardId) {
      return;
    }
    setUpdatingPresentId(entry.id);
    try {
      const entryRef = doc(
        firestoreInstance,
        "tournois",
        tournoi.id,
        "tableaux",
        selectedBoardId,
        "waitingList",
        entry.id
      );
      await updateDoc(
        entryRef,
        present
          ? { present: true, presentAt: serverTimestamp() }
          : { present: false, presentAt: deleteField() }
      );
      setWaitlistEntries((prev) =>
        prev.map((item) =>
          item.id === entry.id
            ? {
                ...item,
                present,
                presentAt: present ? new Date() : undefined,
              }
            : item
        )
      );
    } catch (_error) {
      dispatch(
        showNotification({
          message: "Impossible de mettre à jour la présence du joueur.",
          severity: "error",
        })
      );
    } finally {
      setUpdatingPresentId(null);
    }
  };

  const handleRemoveFromWaitlist = async (entry: WaitlistEntry) => {
    if (!tournoi?.id || !selectedBoardId) {
      return;
    }
    setDeletingEntryId(entry.id);
    try {
      const boardRef = doc(
        firestoreInstance,
        "tournois",
        tournoi.id,
        "tableaux",
        selectedBoardId
      );
      const entryRef = doc(
        firestoreInstance,
        "tournois",
        tournoi.id,
        "tableaux",
        selectedBoardId,
        "waitingList",
        entry.id
      );

      const waitingListCol = collection(
        firestoreInstance,
        "tournois",
        tournoi.id,
        "tableaux",
        selectedBoardId,
        "waitingList"
      );
      const remainingSnapshot = await getDocs(query(waitingListCol));
      const remainingEntries = mapWaitlistSnapshot(remainingSnapshot.docs).filter(
        (item) => item.id !== entry.id
      );

      const batch = writeBatch(firestoreInstance);
      batch.delete(entryRef);
      batch.update(boardRef, { waitlistCount: increment(-1) });
      remainingEntries.forEach((item, index) => {
        const nextPosition = index + 1;
        if (item.waitlistPosition !== nextPosition) {
          batch.update(
            doc(
              firestoreInstance,
              "tournois",
              tournoi.id,
              "tableaux",
              selectedBoardId,
              "waitingList",
              item.id
            ),
            { waitlistPosition: nextPosition }
          );
        }
      });
      await batch.commit();

      setWaitlistEntries(
        remainingEntries.map((item, index) => ({
          ...item,
          waitlistPosition: index + 1,
        }))
      );

      if (
        foundPlayer?.id &&
        String(foundPlayer.id) === entry.id &&
        targetBoardIdForAdd === selectedBoardId
      ) {
        setAlreadyInTargetWaitlist(false);
      }

      await dispatch(fetchBoards()).unwrap();

      dispatch(
        showNotification({
          message: "Joueur retiré de la liste d'attente.",
          severity: "success",
        })
      );
    } catch (_error) {
      dispatch(
        showNotification({
          message: "Impossible de supprimer ce joueur de la liste d'attente.",
          severity: "error",
        })
      );
    } finally {
      setDeletingEntryId(null);
    }
  };

  if (loadingBoards) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "80vh" }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 1, sm: 2 } }}>
      <Stack spacing={2}>
        <Box>
          <Typography
            variant="overline"
            sx={{
              fontWeight: 800,
              letterSpacing: "0.12em",
              color: isDark ? "secondary.light" : "primary.main",
            }}
          >
            Administration
          </Typography>
          <Typography variant="h4" component="h1" fontWeight={800}>
            Listes d'attente par tableau
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Quand une place se libère, inscrivez le joueur via{" "}
            <strong>Inscrire un joueur</strong> (menu admin) en activant si besoin
            le contournement « liste d'attente / places ».
          </Typography>
        </Box>

        {fullBoards.length === 0 ? (
          <Alert severity="info">
            Aucun tableau complet ou avec joueurs en liste d'attente.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.28)" : 2,
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: "0.08em",
                      color: isDark ? "secondary.light" : "primary.main",
                    }}
                  >
                    1 / Consulter
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    Affichage des listes d'attente par tableau
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sélectionnez un tableau pour consulter sa file d'attente et préparer les actions à venir.
                  </Typography>
                </Box>

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", md: "center" }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <FormControl size="small" sx={{ minWidth: 300 }}>
                    <InputLabel>Tableau</InputLabel>
                    <Select value={selectedBoardId} label="Tableau" onChange={handleBoardChange}>
                      {fullBoards.map((board) => (
                        <MenuItem key={String(board.id)} value={String(board.id)}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ width: "100%" }}
                          >
                            <span>
                              {board.name} - {board.label}
                            </span>
                            <Chip
                              size="small"
                              label={getBoardCapacityLabel(board)}
                              color={getBoardCapacityColor(board)}
                              variant="outlined"
                              sx={{ fontWeight: 700 }}
                            />
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {selectedBoard?.heureLimite ? (
                    <Chip
                      icon={<AccessTimeIcon />}
                      color="warning"
                      variant={isDark ? "filled" : "outlined"}
                      label={`Fin de pointage : ${selectedBoard.heureLimite}`}
                      sx={{ fontWeight: 700 }}
                    />
                  ) : null}
                  <Chip
                    color="secondary"
                    variant={isDark ? "filled" : "outlined"}
                    label={`${waitlistEntries.length} en attente`}
                    sx={{ fontWeight: 700 }}
                  />
                  <Chip
                    color="success"
                    variant={isDark ? "filled" : "outlined"}
                    label={`${presentCount} présent${presentCount > 1 ? "s" : ""}`}
                    sx={{ fontWeight: 700 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      void handleRefresh();
                    }}
                    disabled={refreshing || loadingList}
                  >
                    Actualiser
                  </Button>
                </Stack>

                <Paper
                  sx={{
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                  }}
                >
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            bgcolor: isDark
                              ? alpha(theme.palette.common.black, 0.35)
                              : alpha(theme.palette.secondary.main, 0.08),
                          }}
                        >
                          <TableCell sx={{ fontWeight: 700 }}>Place</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Nom</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Licence</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Club</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Date d'ajout</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Présence</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loadingList ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                              <CircularProgress size={22} />
                            </TableCell>
                          </TableRow>
                        ) : waitlistEntries.length > 0 ? (
                          waitlistEntries.map((entry) => (
                            <TableRow
                              key={entry.id}
                              hover
                              sx={{
                                bgcolor: entry.present
                                  ? alpha(theme.palette.success.main, isDark ? 0.16 : 0.08)
                                  : undefined,
                                "&:nth-of-type(even)": {
                                  bgcolor: entry.present
                                    ? alpha(
                                        theme.palette.success.main,
                                        isDark ? 0.2 : 0.1
                                      )
                                    : isDark
                                      ? alpha(theme.palette.common.white, 0.035)
                                      : alpha(theme.palette.common.black, 0.02),
                                },
                              }}
                            >
                              <TableCell sx={{ fontWeight: 700 }}>
                                {entry.waitlistPosition ?? "-"}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                {entry.playerName} {entry.playerSurname}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={entry.licenceId}
                                  variant="outlined"
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>
                              <TableCell>{entry.club || "-"}</TableCell>
                              <TableCell>
                                {entry.createdAt ? entry.createdAt.toLocaleString() : "-"}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  clickable
                                  color={entry.present ? "success" : "default"}
                                  variant={entry.present ? "filled" : "outlined"}
                                  icon={
                                    entry.present ? (
                                      <CheckCircleIcon />
                                    ) : (
                                      <RadioButtonUncheckedIcon />
                                    )
                                  }
                                  label={entry.present ? "Présent" : "Absent"}
                                  disabled={updatingPresentId === entry.id}
                                  onClick={() => {
                                    void handleTogglePresent(
                                      entry,
                                      !entry.present
                                    );
                                  }}
                                  sx={{
                                    fontWeight: 700,
                                    minHeight: 44,
                                    px: 0.5,
                                    "& .MuiChip-label": { px: 1.25 },
                                    "& .MuiChip-icon": { ml: 0.75 },
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  startIcon={<DeleteOutlineIcon />}
                                  disabled={deletingEntryId === entry.id}
                                  onClick={() => setEntryToDelete(entry)}
                                >
                                  Supprimer
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              align="center"
                              sx={{ py: 3, color: "text.secondary" }}
                            >
                              Aucun joueur en liste d'attente pour ce tableau.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Stack>
            </Paper>

            <Paper
              sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.28)" : 2,
                backgroundImage: isDark
                  ? `linear-gradient(120deg, ${alpha(theme.palette.success.main, 0.14)} 0%, ${alpha(theme.palette.background.paper, 0.96)} 62%)`
                  : `linear-gradient(120deg, ${alpha(theme.palette.success.light, 0.2)} 0%, ${theme.palette.background.paper} 62%)`,
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: "0.08em",
                      color: isDark ? "success.light" : "success.dark",
                    }}
                  >
                    2 / Ajouter
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    Ajouter un joueur dans une liste d'attente
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recherchez un joueur par numéro de licence puis choisissez un tableau complet accessible.
                  </Typography>
                </Box>

                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <TextField
                    size="small"
                    label="Numéro de licence"
                    value={licenceInput}
                    onChange={(event) => setLicenceInput(event.target.value)}
                  />
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleSearchPlayer}
                    disabled={loadingPlayer || !licenceInput.trim()}
                  >
                    {loadingPlayer ? "Recherche..." : "Rechercher"}
                  </Button>
                </Stack>

                {loadingPlayer && (
                  <LinearProgress
                    sx={{
                      borderRadius: 999,
                      bgcolor: isDark
                        ? alpha(theme.palette.secondary.main, 0.24)
                        : alpha(theme.palette.secondary.main, 0.16),
                    }}
                  />
                )}

                {foundPlayer && (
                  <Stack spacing={2}>
                    <Alert
                      severity="success"
                      sx={{
                        borderLeft: `4px solid ${theme.palette.success.main}`,
                        bgcolor: isDark
                          ? alpha(theme.palette.success.main, 0.14)
                          : alpha(theme.palette.success.main, 0.08),
                      }}
                    >
                      Joueur trouvé: {foundPlayer.name} {foundPlayer.surname}
                    </Alert>
                    {loadingPlayer ? (
                      <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                        sx={{ color: "text.secondary" }}
                      >
                        <CircularProgress size={18} />
                        <Typography variant="body2">
                          Vérification des tableaux accessibles...
                        </Typography>
                      </Stack>
                    ) : playerSearchDone && eligibleFullBoards.length === 0 ? (
                      <Alert severity="warning">
                        Aucun tableau complet accessible avec file d'attente activée
                        pour ce joueur.
                      </Alert>
                    ) : (
                      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                        <FormControl size="small" sx={{ minWidth: 300 }}>
                          <InputLabel>Tableau cible</InputLabel>
                          <Select
                            value={targetBoardIdForAdd}
                            label="Tableau cible"
                            onChange={(event) => setTargetBoardIdForAdd(event.target.value)}
                          >
                            {eligibleFullBoards.map((board) => (
                              <MenuItem key={String(board.id)} value={String(board.id)}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  justifyContent="space-between"
                                  sx={{ width: "100%" }}
                                >
                                  <span>
                                    {board.name} - {board.label}
                                  </span>
                                  <Chip
                                    size="small"
                                    label={getBoardCapacityLabel(board)}
                                    color={getBoardCapacityColor(board)}
                                    variant="outlined"
                                    sx={{ fontWeight: 700 }}
                                  />
                                </Stack>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {alreadyInTargetWaitlist ? (
                          <Alert severity="info" sx={{ py: 0.25 }}>
                            Ce joueur est déjà en liste d'attente sur ce tableau.
                          </Alert>
                        ) : alreadyRegisteredOnTargetBoard ? (
                          <Alert severity="warning" sx={{ py: 0.25 }}>
                            Ce joueur est déjà inscrit sur ce tableau.
                          </Alert>
                        ) : (
                          <Button
                            variant="contained"
                            color="success"
                            onClick={handleAddToWaitlist}
                            disabled={!targetBoardIdForAdd || addingPlayer}
                          >
                            {addingPlayer
                              ? "Ajout en cours..."
                              : "Ajouter en liste d'attente"}
                          </Button>
                        )}
                      </Stack>
                    )}
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Stack>
        )}
      </Stack>
      <ConfirmDialog
        open={entryToDelete !== null}
        title="Retirer de la liste d'attente"
        message={
          entryToDelete
            ? `Retirer ${entryToDelete.playerName} ${entryToDelete.playerSurname} de la liste d'attente ?`
            : ""
        }
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        confirmColor="error"
        onCancel={() => setEntryToDelete(null)}
        onConfirm={() => {
          if (entryToDelete) {
            void handleRemoveFromWaitlist(entryToDelete);
          }
          setEntryToDelete(null);
        }}
      />
    </Container>
  );
};

export default AdminWaitlist;
